import type { Language } from "~/api/model";

/**
 * Localized section titles for the default Summary template.
 * Maps language keys to section title translations.
 */
const DEFAULT_SUMMARY_SECTION_TITLES: Record<string, Record<string, string>> = {
  en: { OVERVIEW: "OVERVIEW", CONCLUSION: "CONCLUSION" },
  fr: { OVERVIEW: "APERÇU", CONCLUSION: "CONCLUSION" },
  es: { OVERVIEW: "RESUMEN", CONCLUSION: "CONCLUSIÓN" },
};

/**
 * Maps an organization's Output Language setting to a base language key.
 * @param outputLanguage - The organization's language setting (e.g., 'FR', 'FR_CA', 'EN', etc.)
 * @returns A base language key ('en', 'fr', or 'es')
 */
function getLanguageKey(
  outputLanguage: Language | string | undefined | null
): "en" | "fr" | "es" {
  if (!outputLanguage) return "en";
  const lang = outputLanguage.toLowerCase();
  if (lang.startsWith("fr")) return "fr";
  if (lang.startsWith("es")) return "es";
  return "en";
}

/**
 * Localizes a report section title if it matches a known default Summary template section.
 * This function only localizes titles for the default "Summary" template to avoid
 * unexpectedly changing user-defined template section titles.
 *
 * @param title - The original section title from the backend
 * @param templateName - The name of the template (used to identify the default Summary template)
 * @param outputLanguage - The organization's Output Language setting
 * @returns The localized title if applicable, otherwise the original title
 */
export function localizeReportSectionTitle(
  title: string | undefined,
  templateName: string | undefined | null,
  outputLanguage: Language | string | undefined | null
): string {
  if (!title) return "";

  // Only localize titles for the default "Summary" template
  if (templateName !== "Summary") {
    return title;
  }

  // Check if the title matches a known default section title (case-insensitive)
  const normalizedTitle = title.toUpperCase();
  const langKey = getLanguageKey(outputLanguage);
  const localized = DEFAULT_SUMMARY_SECTION_TITLES[langKey]?.[normalizedTitle];

  return localized ?? title;
}

/**
 * Checks if a template is the default Summary template.
 * @param templateName - The name of the template
 * @returns true if the template is the default Summary template
 */
export function isDefaultSummaryTemplate(
  templateName: string | undefined | null
): boolean {
  return templateName === "Summary";
}
